import React from "react";
import { motion } from "framer-motion";

/**
 * Unified editorial-style section header used across the site.
 * Matches the Research Domains aesthetic — small uppercase eyebrow,
 * large light title with optional italic serif accent, compact subtitle.
 */
const SectionHeader = ({
  eyebrow,
  title,
  titleAccent,
  subtitle,
  align = "left",
  className = "",
  animateWords = false,
  index,
  theme = "light",
}) => {
  const alignCls = align === "center" ? "text-center mx-auto" : "";
  const isDark = theme === "dark";
  const c = isDark
    ? {
        indexText: "text-white/40",
        rule: "bg-white/60",
        eyebrow: "text-white/70",
        title: "text-white",
        accent: "text-white/35",
        subtitle: "text-white/60",
      }
    : {
        indexText: "text-gray-400",
        rule: "bg-gray-900",
        eyebrow: "text-gray-600",
        title: "text-gray-900",
        accent: "text-gray-400",
        subtitle: "text-gray-600",
      };

  return (
    <div className={`${alignCls} ${className}`}>
      <div
        className={`flex items-center gap-4 mb-5 ${
          align === "center" ? "justify-center" : ""
        }`}
      >
        {index && (
          <span className={`text-[10px] font-mono tracking-[0.25em] uppercase ${c.indexText}`}>
            § {String(index).padStart(2, "0")}
          </span>
        )}
        <span className={`h-px w-10 ${c.rule}`} />
        <span className={`text-xs tracking-[0.25em] uppercase font-medium ${c.eyebrow}`}>
          {eyebrow}
        </span>
      </div>

      <div className="max-w-3xl">
        <h2 className={`text-4xl sm:text-5xl lg:text-6xl font-light mb-5 tracking-tight leading-[1.05] ${c.title}`}>
          {animateWords ? <AnimatedTitle text={title} accent={titleAccent} theme={theme} /> : (
            <>
              {title}
              {titleAccent && (
                <>
                  <br />
                  <span className={`font-light ${c.accent}`}>
                    {titleAccent}
                  </span>
                </>
              )}
            </>
          )}
        </h2>

        {subtitle && (
          <p className={`text-base md:text-lg font-light leading-relaxed max-w-2xl ${c.subtitle}`}>
            {subtitle}
          </p>
        )}
      </div>
    </div>
  );
};

/* Animated split-text title — word-by-word reveal */
const AnimatedTitle = ({ text, accent, theme = "light" }) => {
  const words = text.split(" ");
  const accentWords = accent ? accent.split(" ") : [];
  const accentCls = theme === "dark" ? "text-white/35" : "text-gray-400";

  return (
    <>
      {words.map((word, i) => (
        <motion.span
          key={`w-${i}`}
          initial={{ opacity: 0, y: 20, filter: "blur(4px)" }}
          whileInView={{ opacity: 1, y: 0, filter: "blur(0px)" }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{
            duration: 0.6,
            delay: i * 0.06,
            ease: [0.22, 1, 0.36, 1],
          }}
          className="inline-block mr-[0.25em]"
        >
          {word}
        </motion.span>
      ))}
      {accent && (
        <>
          <br />
          <span className={`font-light ${accentCls}`}>
            {accentWords.map((word, i) => (
              <motion.span
                key={`a-${i}`}
                initial={{ opacity: 0, y: 20, filter: "blur(4px)" }}
                whileInView={{ opacity: 1, y: 0, filter: "blur(0px)" }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{
                  duration: 0.6,
                  delay: (words.length + i) * 0.06,
                  ease: [0.22, 1, 0.36, 1],
                }}
                className="inline-block mr-[0.25em]"
              >
                {word}
              </motion.span>
            ))}
          </span>
        </>
      )}
    </>
  );
};

export default SectionHeader;
